<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '../../stores/user'
import { ReviewsApi } from '../../services/reviews'
import { useToast } from 'vuestic-ui'

const { init: toast } = useToast()
const user = useUserStore()

const open = ref(false)
const text = ref('')
const publish = ref(true)
const sending = ref(false)
const hasReviewed = ref<boolean | null>(null)

const LS_KEY = 'mp_review_done'

const isAdmin = computed(() => user.isTenantAdmin || user.hasRole?.('Admin'))
const visible = computed(() => user.loaded && isAdmin.value && hasReviewed.value === false)

async function loadStatus() {
  if (localStorage.getItem(LS_KEY) === '1') {
    hasReviewed.value = true
  } else {
    hasReviewed.value = null
  }

  try {
    const { data } = await ReviewsApi.status()
    hasReviewed.value = !!data?.alreadyReviewed
    if (hasReviewed.value) localStorage.setItem(LS_KEY, '1')
  } catch {
    hasReviewed.value = false
  }
}

onMounted(async () => {
  if (!user.loaded) await user.ensureInitialized()
  await loadStatus()
})

function remaining() {
  return 250 - (text.value?.length || 0)
}

async function submit() {
  if (!text.value?.trim()) {
    toast({ message: 'Değerlendirme metni zorunludur.', color: 'warning' })
    return
  }
  if (text.value.length > 250) {
    toast({ message: 'En fazla 250 karakter yazabilirsiniz.', color: 'warning' })
    return
  }
  try {
    sending.value = true
    await ReviewsApi.submit({ content: text.value.trim(), publishAllowed: publish.value })
    toast({ message: 'Değerlendirmeniz için teşekkürler!', color: 'success' })
    hasReviewed.value = true
    localStorage.setItem(LS_KEY, '1')
    open.value = false
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <button v-if="visible" class="review-fab" aria-label="Bizi Değerlendir" @click="open = true">
    <VaIcon name="mso-rate_review" size="22px" />
  </button>

  <VaModal v-model="open" title="Menux Plus İşletmesini Değerlendirin" hide-default-actions size="small">
    <div class="space-y-3">
      <VaTextarea
        v-model="text"
        label="Değerlendirmeniz"
        :max-length="250"
        :autosize="true"
        :min-rows="3"
        :max-rows="5"
        class="w-full"
      />
      <div class="flex justify-between text-xs opacity-70">
        <span>En fazla 250 karakter</span>
        <span>{{ remaining() }}</span>
      </div>

      <VaCheckbox v-model="publish">
        <template #label>
          Değerlendirmem
          <a
            href="https://menuxplus.com"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary font-semibold underline hover:no-underline"
          >
            Menux Plus
          </a>
          sitesinde yayınlanabilir.
        </template>
      </VaCheckbox>

      <div class="flex justify-end gap-2">
        <VaButton preset="secondary" @click="open = false">Vazgeç</VaButton>
        <VaButton :loading="sending" @click="submit">Gönder</VaButton>
      </div>
    </div>
  </VaModal>
</template>

<style scoped>
.review-fab {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 2147483647;
  width: 52px;
  height: 52px;
  border-radius: 9999px;
  border: none;
  background: var(--va-primary);
  color: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.review-fab:hover {
  filter: brightness(1.05);
}
</style>
